import Card from "@/components/common/Card";
import { TrophyIcon, StarIcon, AcademicCapIcon } from "@heroicons/react/24/outline";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

interface AchievementsTabProps {
  user: any;
}

const AchievementsTab = ({ user }: AchievementsTabProps) => {
  const { t } = useTranslation();

  const achievements = user?.achievements || [];
  const certifications = user?.certifications || [];
  const badges = user?.badges || [];

  return (
    <Card className="backdrop-blur-sm bg-white/80 dark:bg-gray-800/80 border-0 shadow-xl hover:shadow-2xl transition-all duration-300">
      <div className="p-6">
        <h3 className="mb-6 text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent dark:from-white dark:to-gray-300">
          {t("profile.achievements")}
        </h3>

        <div className="space-y-8">
          {/* Certifications Section */}
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <AcademicCapIcon className="h-6 w-6 text-primary-600" />
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t("profile.certifications")}
              </h4>
              <span className="bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-200 px-2 py-1 rounded-full text-sm font-medium">
                {certifications.length}
              </span>
            </div>

            {certifications.length > 0 ? (
              <div className="grid gap-4 md:grid-cols-2">
                {certifications.map((cert: any, index: number) => (
                  <motion.div
                    key={cert._id || index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="bg-gradient-to-r from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20 rounded-lg p-4 border border-primary-200 dark:border-primary-700"
                  >
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        <div className="h-10 w-10 rounded-full bg-primary-600 flex items-center justify-center">
                          <AcademicCapIcon className="h-5 w-5 text-white" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h5 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {cert.name || cert.title || t("profile.unnamedCertification")}
                        </h5>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          {cert.issuer || cert.organization || t("profile.unknownIssuer")}
                        </p>
                        {cert.issuedDate && (
                          <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                            {new Date(cert.issuedDate).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <AcademicCapIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-400">
                  {t("profile.noCertifications")}
                </p>
              </div>
            )}
          </div>

          {/* Badges Section */}
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <StarIcon className="h-6 w-6 text-yellow-500" />
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t("profile.badges")}
              </h4>
              <span className="bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 px-2 py-1 rounded-full text-sm font-medium">
                {badges.length}
              </span>
            </div>

            {badges.length > 0 ? (
              <div className="grid gap-4 md:grid-cols-3">
                {badges.map((badge: any, index: number) => (
                  <motion.div
                    key={badge._id || index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="bg-gradient-to-r from-yellow-50 to-yellow-100 dark:from-yellow-900/20 dark:to-yellow-800/20 rounded-lg p-4 border border-yellow-200 dark:border-yellow-700 text-center"
                  >
                    <div className="flex flex-col items-center space-y-2">
                      <div className="h-12 w-12 rounded-full bg-yellow-500 flex items-center justify-center">
                        <StarIcon className="h-6 w-6 text-white" />
                      </div>
                      <h5 className="text-sm font-medium text-gray-900 dark:text-white">
                        {badge.name || badge.title || t("profile.unnamedBadge")}
                      </h5>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        {badge.description || t("profile.noDescription")}
                      </p>
                      {badge.earnedDate && (
                        <p className="text-xs text-gray-500 dark:text-gray-500">
                          {new Date(badge.earnedDate).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <StarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-400">
                  {t("profile.noBadges")}
                </p>
              </div>
            )}
          </div>

          {/* Achievements Section */}
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <TrophyIcon className="h-6 w-6 text-orange-500" />
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t("profile.achievements")}
              </h4>
              <span className="bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200 px-2 py-1 rounded-full text-sm font-medium">
                {achievements.length}
              </span>
            </div>

            {achievements.length > 0 ? (
              <div className="grid gap-4 md:grid-cols-2">
                {achievements.map((achievement: any, index: number) => (
                  <motion.div
                    key={achievement._id || index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="bg-gradient-to-r from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20 rounded-lg p-4 border border-orange-200 dark:border-orange-700"
                  >
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        <div className="h-10 w-10 rounded-full bg-orange-500 flex items-center justify-center">
                          <TrophyIcon className="h-5 w-5 text-white" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h5 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {achievement.name || achievement.title || t("profile.unnamedAchievement")}
                        </h5>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          {achievement.description || t("profile.noDescription")}
                        </p>
                        {achievement.earnedDate && (
                          <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                            {new Date(achievement.earnedDate).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <TrophyIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-400">
                  {t("profile.noAchievements")}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

export default AchievementsTab;
